import * as angular from "angular";
import {IController, IOnChangesObject} from "angular";
import * as _ from "lodash";
import {BooleanMap, PodSettingsMap, getMandatoryKey, isMandatoryFlagKey} from "../../utils/identifications.util";

export class identificationsMultiSelectCtrl implements IController {

  identifications: BooleanMap;
  options: PodSettingsMap;
  noLabel: boolean;
  onChange: (event: { identifications: BooleanMap }) => void;

  public label: string;
  public identificationValues: string[];
  public requiredIdentification: BooleanMap = {};
  public mandatoryValues: BooleanMap = {};
  public selectedMap: BooleanMap = {};

  // Real POD keys only (no `is*FillRequired` flags). Values keep the raw
  // status enum so the template can lock `"Required"` PODs.
  public podOptions: PodSettingsMap = {};

  /* @ngInject */
  constructor(private $mdSelect) {
  }

  $onInit() {
    this.label = this.noLabel ? undefined : 'Proof Of Delivery';
    this._refresh();
  }

  $onChanges(changes: IOnChangesObject) {
    if (changes.options && !changes.options.isFirstChange()) {
      this._refresh();
    }
  }

  // Fires on every checkbox toggle inside the open dropdown. Keeps the
  // Mandatory switch state in sync live: a POD that gets unchecked must drop
  // its Mandatory flag immediately, both visually and in `mandatoryValues`.
  onSelectionChange() {
    this._updateSelectedMap();
    _.forOwn(this.podOptions, (_value, key) => {
      if (!this.selectedMap[key]) {
        this.mandatoryValues[key] = false;
      }
    });
  }

  onSelect(updatedIdentifications: string[]) {
    this._emitChange(updatedIdentifications);
    this.$mdSelect.hide();
  }

  onMandatoryChange() {
    this._emitChange(this.identificationValues);
  }

  onDone() {
    this.$mdSelect.hide();
  }

  onReset() {
    this.identifications = this.requiredIdentification;
    this.identificationValues = this._toSelectedKeys(this.requiredIdentification);
    this.mandatoryValues = {};
    this._updateSelectedMap();
    this.$mdSelect.hide();
  }

  // ******************************
  //        Private methods
  // ******************************

  private _refresh() {
    this.podOptions = _.pickBy(this.options, (_value, key) => !isMandatoryFlagKey(key));
    this.requiredIdentification = _.mapValues(this.podOptions, (value) => value === 'Required');
    this.identificationValues = this._toSelectedKeys(this.identifications);
    this.mandatoryValues = this._buildMandatoryValues(this.identifications);
    this._updateSelectedMap();
  }

  private _emitChange(updatedIdentifications: string[]) {
    const identifications: BooleanMap = {};
    _.forOwn(this.podOptions, (_value, key) => {
      const isSelected = _.includes(updatedIdentifications, key);
      identifications[key] = isSelected;
      identifications[getMandatoryKey(key)] = isSelected && !!this.mandatoryValues[key];
    });
    this.onChange({identifications});
  }

  private _updateSelectedMap() {
    this.selectedMap = _.reduce(this.identificationValues, (acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as BooleanMap);
  }

  private _toSelectedKeys(identificationKeys: BooleanMap): string[] {
    return _.chain(identificationKeys)
      .pickBy((value, key) => value === true && _.has(this.podOptions, key))
      .keys()
      .value();
  }

  // Selected PODs reflect their current `identifications` value; unselected
  // PODs fall back to the backend default (`is{PodKey}FillRequired` carried
  // as metadata on `options`) so flipping an Optional POD on starts the
  // Mandatory switch in its configured default position.
  private _buildMandatoryValues(identifications: BooleanMap): BooleanMap {
    return _.mapValues(this.podOptions, (_value, key) => {
      const mandatoryKey = getMandatoryKey(key);
      return _.get(identifications, key)
        ? !!_.get(identifications, mandatoryKey)
        : !!_.get(this.options, mandatoryKey);
    });
  }

}

angular
  .module('bringoz.admin.orders')
  .component('identificationsMultiSelect', {
    bindings: {
      identifications: '<',
      options: '<?',
      noLabel: "<?",
      onChange: '&'
    },
    template: require('./identifications-multi-select.html').default,
    controller: identificationsMultiSelectCtrl
  });
